import { Plus, Trash2 } from "lucide-react";
import { useId, useState } from "react";

import {
  LISTENER_GENDERS,
  type ListenerGender,
  type NiqqudTable,
  type SpeechPronunciationDraft,
} from "@/lib/speechContent";
import { Button } from "@/ui/Button";
import { IconButton } from "@/ui/IconButton";
import { Field, Input, Label } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { Tooltip } from "@/ui/Tooltip";

/**
 * The Pronunciation tab: THREE named groups, not two tables (#870).
 *
 * ## Why the group names are what they are
 *
 * The S7 contract (§7) states the shape and states the failure: the
 * pronunciation document has "all four top-level fields", and "female/male
 * tables alone are incomplete". Each of the four is a different question the
 * runtime asks, so each gets its own named control rather than a row in one
 * undifferentiated map:
 *
 *  * **Listener** — `listener_homographs.{female,male}`, chosen by the
 *    PATIENT's recorded genderform. `tts_text._gender_homograph_subs` looks the
 *    conversation's genderform up in this map, so an entry present for one
 *    gender and absent for the other is a word the runtime speaks two different
 *    ways depending on who is listening. The two tables therefore sit side by
 *    side under one heading, and the asymmetry is reported where it is created.
 *  * **Speaker** — the anchor (`אני` in both source packs) and the table that
 *    applies only after it. This is Liv talking about herself, and the anchor is
 *    what SCOPES it: without one, `_speaker_homograph_subs` returns early and
 *    the table never applies. So the anchor is a field of this group rather than
 *    a setting somewhere else.
 *  * **General** — `general_niqqud`, applied to every message with no gender
 *    keying at all: loanwords and fixed-feminine participles the engine
 *    mis-vocalizes regardless of who is speaking or listening.
 *
 * ## AN EMPTY TABLE IS AUTHORED, AND SAYS SO
 *
 * `speech_profile.SpeechPronunciation` reads an empty table as a complete
 * statement — "this profile substitutes nothing in this group" — and so does the
 * pinned engine's own predicate. So an empty group renders as an empty table
 * with its count, not as an unfinished-looking blank: the completeness a
 * clinician needs to see is that all four groups are DECLARED, which they always
 * are once the document exists.
 *
 * ## A BARE FORM IS IDENTITY AND IS NOT EDITED IN PLACE
 *
 * An entry IS its bare form — it is the key `tts_text` interpolates into
 * `rf"\b{re.escape(bare)}\b"` — so changing it is removing one entry and adding
 * another, which is exactly the rule `SubstitutionTables` states for a find
 * text. The field is read-only and "Add word" is where a bare form is entered,
 * once, against a duplicate check.
 *
 * ## NOTHING IS TRIMMED OR NORMALIZED
 *
 * Hebrew niqqud are combining marks, and a control that "tidied" a value would
 * silently change what the engine speaks. Values travel through this component
 * as the literal strings they are; the only rejection is an empty bare form,
 * which is not a word.
 */

/** How many rows of one table render before the clinician asks for the rest. */
const VISIBLE_ROW_LIMIT = 25;

const GENDER_LABELS: Record<ListenerGender, string> = {
  female: "Female listener",
  male: "Male listener",
};

/**
 * One bare-form → vocalized-form table, with its own add control.
 *
 * Presentational and total: it owns no draft and performs no request, so the
 * same component renders the authoring surface and the read-only one and
 * `readOnly` is the single prop every control reads.
 */
function NiqqudTableEditor({
  table,
  label,
  describedBy,
  emptyDescription,
  readOnly,
  onSet,
}: {
  table: NiqqudTable;
  label: string;
  describedBy?: string | undefined;
  emptyDescription: string;
  readOnly: boolean;
  /** `null` removes the entry; any string — including `""` — sets it. */
  onSet: (bare: string, vocalized: string | null) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [draftBare, setDraftBare] = useState("");
  const [filter, setFilter] = useState("");
  const [showAll, setShowAll] = useState(false);
  const headingId = useId();
  const filterId = useId();
  const addBareId = useId();
  const addProblemId = useId();

  const entries = Object.entries(table);
  const needle = filter.trim();
  const matched =
    needle === ""
      ? entries
      : entries.filter(
          ([bare, vocalized]) =>
            bare.includes(needle) || vocalized.includes(needle),
        );
  const visible = showAll ? matched : matched.slice(0, VISIBLE_ROW_LIMIT);
  // `Object.hasOwn`, never `in`: `in` walks the prototype chain, so a clinician
  // typing `toString`, `constructor` or `valueOf` was told the word was already
  // in a table it has never been in — and the Add control stayed disabled with
  // no way to proceed. The table's own entries are own properties (`readTable`
  // builds them with `Object.fromEntries` for the matching reason), so ownership
  // is exactly the question being asked.
  //
  // Compared against `draftBare` rather than a trimmed copy, because `draftBare`
  // is what `onSet` stores: a check against a value the add would not write
  // could report a collision that cannot happen, or miss one that can.
  const duplicate = draftBare.trim() !== "" && Object.hasOwn(table, draftBare);

  return (
    <section
      aria-labelledby={headingId}
      data-slot="niqqud-table"
      className="rounded-md border border-border bg-card"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-3 py-2">
        <h3 id={headingId} className="text-sm font-medium text-foreground">
          {label}
        </h3>
        <span className="text-xs text-muted-foreground">
          {entries.length} {entries.length === 1 ? "word" : "words"}
        </span>
        {entries.length > VISIBLE_ROW_LIMIT ? (
          <span className="ml-auto flex items-center gap-2">
            <Label htmlFor={filterId} className="text-xs">
              Filter
            </Label>
            <Input
              id={filterId}
              dir="auto"
              className="h-8 w-40"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            />
          </span>
        ) : null}
      </div>

      {entries.length === 0 ? (
        // AN EMPTY GROUP IS A STATEMENT, so it says what it means rather than
        // reading as an unfinished form. `speech_profile.SpeechPronunciation`
        // and the pinned engine both accept it as complete.
        <p className="px-3 py-2 text-sm text-muted-foreground">
          {emptyDescription}
        </p>
      ) : (
        <ul aria-labelledby={headingId} {...(describedBy ? { "aria-describedby": describedBy } : {})}>
          {visible.map(([bare, vocalized]) => (
            <NiqqudRow
              key={bare}
              bare={bare}
              vocalized={vocalized}
              readOnly={readOnly}
              onVocalized={(value) => onSet(bare, value)}
              onRemove={() => onSet(bare, null)}
            />
          ))}
        </ul>
      )}

      {matched.length > visible.length ? (
        <div className="border-t border-border px-3 py-2">
          <Button variant="ghost" size="sm" onClick={() => setShowAll(true)}>
            Show all {matched.length} words
          </Button>
        </div>
      ) : null}

      {readOnly ? null : (
        <div className="border-t border-border px-3 py-2">
          {adding ? (
            <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
              <div className="space-y-1">
                <Label htmlFor={addBareId} className="text-xs">
                  Word as it is written
                </Label>
                <Input
                  id={addBareId}
                  dir="auto"
                  value={draftBare}
                  aria-invalid={duplicate || undefined}
                  aria-describedby={duplicate ? addProblemId : undefined}
                  onChange={(event) => setDraftBare(event.target.value)}
                />
                {duplicate ? (
                  <p id={addProblemId} className="text-sm text-destructive">
                    This word is already in the table. Edit its pronunciation
                    below instead.
                  </p>
                ) : null}
              </div>
              <div className="flex items-center gap-2 sm:pb-1">
                <Button
                  size="sm"
                  disabled={draftBare.trim() === "" || duplicate}
                  onClick={() => {
                    // The vocalized form opens EMPTY and is typed in the row
                    // below, which is where every other pronunciation is edited.
                    // Asking for both here would make the add control a second,
                    // differently-shaped editor for the same pair.
                    // VERBATIM, not trimmed. This module's own contract is
                    // that nothing here trims or normalizes, and a bare form is
                    // no exception: it is the key `tts_text` interpolates into
                    // `rf"\b{re.escape(bare)}\b"`, the service stores it as
                    // written (`_not_blank` explicitly returns the original
                    // rather than a stripped copy), and a browser that quietly
                    // rewrote it would make the stored document differ from the
                    // one the clinician typed. The blank guard below stays
                    // `.trim()`, because whitespace alone is not a word and is
                    // what the service refuses — that is a question ABOUT the
                    // value, not an edit to it.
                    onSet(draftBare, "");
                    setDraftBare("");
                    setAdding(false);
                    // AND THE NEW ROW HAS TO BE ON SCREEN. A new entry is
                    // appended, so in a table already over the row cap it lands
                    // outside the default view — and the field where its
                    // pronunciation is typed would not exist in the document at
                    // all. The clinician would have added a word spelled exactly
                    // as it already is, with no way to vocalize it and nothing
                    // saying why. Lifting the cap is what makes "Add" complete
                    // the action it started; the filter and the count are
                    // unaffected.
                    setShowAll(true);
                    setFilter("");
                  }}
                >
                  Add
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setDraftBare("");
                    setAdding(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
              <Plus aria-hidden="true" className="h-4 w-4" />
              Add word to {label.toLowerCase()}
            </Button>
          )}
        </div>
      )}
    </section>
  );
}

function NiqqudRow({
  bare,
  vocalized,
  readOnly,
  onVocalized,
  onRemove,
}: {
  bare: string;
  vocalized: string;
  readOnly: boolean;
  onVocalized: (value: string) => void;
  onRemove: () => void;
}) {
  const bareId = useId();
  const vocalizedId = useId();

  return (
    <li className="grid gap-2 border-b border-border px-3 py-2 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
      <div className="space-y-1">
        <Label htmlFor={bareId} className="text-xs">
          Written
        </Label>
        {/* Read-only AND visibly so: a bare form is the entry's identity —
            the key the provider compiles into a whole-word pattern — so
            changing it is removing one entry and adding another. A field that
            looked editable would invite an edit the control will not take. */}
        <Input
          id={bareId}
          dir="auto"
          className="bg-muted text-muted-foreground"
          value={bare}
          readOnly
          aria-disabled="true"
          onChange={() => undefined}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor={vocalizedId} className="text-xs">
          Spoken (with niqqud)
        </Label>
        <Input
          id={vocalizedId}
          dir="auto"
          value={vocalized}
          readOnly={readOnly}
          aria-disabled={readOnly || undefined}
          onChange={(event) => onVocalized(event.target.value)}
        />
      </div>
      <div className="flex items-center sm:pb-1">
        {readOnly ? null : (
          <Tooltip content={`Remove ${bare} from this table`}>
            <IconButton
              label={`Remove ${bare}`}
              icon={<Trash2 className="h-4 w-4" />}
              onClick={onRemove}
            />
          </Tooltip>
        )}
      </div>
    </li>
  );
}

/** One entry set on a table, returned as a new table. `null` removes it. */
function withEntry(
  table: NiqqudTable,
  bare: string,
  vocalized: string | null,
): NiqqudTable {
  const pairs = Object.entries(table).filter(([form]) => form !== bare);
  if (vocalized !== null) {
    pairs.push([bare, vocalized]);
  }
  // `fromEntries`, never assignment, for `readTable`'s reason: a bare form is
  // clinician-authored text and `__proto__` as an assignment target would not
  // become an own property.
  return Object.fromEntries(pairs) as NiqqudTable;
}

export function SpeechPronunciationGroups({
  draft,
  readOnly,
  onChange,
}: {
  draft: SpeechPronunciationDraft;
  readOnly: boolean;
  onChange: (next: SpeechPronunciationDraft) => void;
}) {
  const listenerHeadingId = useId();
  const speakerHeadingId = useId();
  const generalHeadingId = useId();
  const symmetryId = useId();

  const female = Object.keys(draft.listener.female);
  const male = Object.keys(draft.listener.male);
  // `Object.hasOwn`, never `in` — the rule stated in full over the Add control
  // above, and these two lines were its exception. They do not only decide
  // whether the notice renders: their LENGTHS are the counts a clinician reads
  // ("Missing from the male table: N words"), so an inherited name subtracted
  // from a number on screen while the service still refused the Save.
  const missingInMale = female.filter(
    (form) => !Object.hasOwn(draft.listener.male, form),
  );
  const missingInFemale = male.filter(
    (form) => !Object.hasOwn(draft.listener.female, form),
  );
  const asymmetric = missingInMale.length > 0 || missingInFemale.length > 0;

  return (
    <div className="space-y-6">
      <section aria-labelledby={listenerHeadingId} className="space-y-2">
        <h2 id={listenerHeadingId} className="text-sm font-semibold text-foreground">
          Listener
        </h2>
        <p id={symmetryId} className="text-sm text-muted-foreground">
          Words addressed to the patient, spoken differently depending on the
          patient&apos;s recorded gender. Both tables have to cover the same
          words: the runtime picks one table per conversation, so a word in only
          one of them is spoken two different ways depending on who is listening.
        </p>

        {asymmetric ? (
          // THE SERVICE REFUSES THIS, and it refuses it at Save with a count.
          // Stated here as well because the remedy is on this screen and the
          // clinician is standing in front of it — the same reason the Rules
          // surface states a missing required language beside its controls.
          <Notice tone="warning">
            <p>
              The two listener tables do not cover the same words, so this
              profile cannot be saved yet.
            </p>
            {missingInMale.length > 0 ? (
              <p className="mt-1">
                Missing from the male table: {missingInMale.length}{" "}
                {missingInMale.length === 1 ? "word" : "words"}.
              </p>
            ) : null}
            {missingInFemale.length > 0 ? (
              <p className="mt-1">
                Missing from the female table: {missingInFemale.length}{" "}
                {missingInFemale.length === 1 ? "word" : "words"}.
              </p>
            ) : null}
          </Notice>
        ) : null}

        <div className="grid gap-3 lg:grid-cols-2">
          {LISTENER_GENDERS.map((gender) => (
            <NiqqudTableEditor
              key={gender}
              table={draft.listener[gender]}
              label={GENDER_LABELS[gender]}
              describedBy={symmetryId}
              emptyDescription="No words yet. This table applies to conversations with a patient of this recorded gender."
              readOnly={readOnly}
              onSet={(bare, vocalized) =>
                onChange({
                  ...draft,
                  listener: {
                    ...draft.listener,
                    [gender]: withEntry(draft.listener[gender], bare, vocalized),
                  },
                })
              }
            />
          ))}
        </div>
      </section>

      <section aria-labelledby={speakerHeadingId} className="space-y-2">
        <h2 id={speakerHeadingId} className="text-sm font-semibold text-foreground">
          Speaker
        </h2>
        <p className="text-sm text-muted-foreground">
          Words Liv uses about herself. They are only pronounced this way
          directly after the anchor word below, so the same word spoken about
          the patient is untouched.
        </p>

        <Field
          label="Anchor word"
          description="The word that has to come immediately before, for the table below to apply. Without it, nothing in that table is used."
        >
          {({ inputId, describedBy }) => (
            <Input
              id={inputId}
              dir="auto"
              aria-describedby={describedBy}
              value={draft.speakerAnchor}
              readOnly={readOnly}
              aria-disabled={readOnly || undefined}
              onChange={(event) =>
                onChange({ ...draft, speakerAnchor: event.target.value })
              }
            />
          )}
        </Field>

        <NiqqudTableEditor
          table={draft.speakerAfterAnchor}
          label="Words after the anchor"
          emptyDescription="No words yet. Words added here are only pronounced this way directly after the anchor word."
          readOnly={readOnly}
          onSet={(bare, vocalized) =>
            onChange({
              ...draft,
              speakerAfterAnchor: withEntry(
                draft.speakerAfterAnchor,
                bare,
                vocalized,
              ),
            })
          }
        />
      </section>

      <section aria-labelledby={generalHeadingId} className="space-y-2">
        <h2 id={generalHeadingId} className="text-sm font-semibold text-foreground">
          General
        </h2>
        <p className="text-sm text-muted-foreground">
          Words pronounced the same way in every message, whoever is speaking or
          listening — loanwords and clinical terms a voice otherwise reads
          wrongly.
        </p>
        <NiqqudTableEditor
          table={draft.generalNiqqud}
          label="General niqqud"
          emptyDescription="No words yet. Words added here are pronounced this way in every message."
          readOnly={readOnly}
          onSet={(bare, vocalized) =>
            onChange({
              ...draft,
              generalNiqqud: withEntry(draft.generalNiqqud, bare, vocalized),
            })
          }
        />
      </section>
    </div>
  );
}
